import * as _ from "lodash";
import { AUTOSIZE_KEY, DEFAULT_SHAPE_PROPERTIES, TAG_STYLES } from "../../Constants";
import { IAnyObjectType } from "../../widgets/components/SelectionBox";
import { ShapeType } from "../../widgets/interfaces/model/enumerations/index";
import { INonUndoableAction } from "../../widgets/interfaces/model/index";
import { IShapeProperties } from "../../widgets/interfaces/model/properties/index";
import { ISelection } from "../../widgets/interfaces/model/Selection";
import { deselectAllTableElements } from "../../widgets/util";

/**
 * Adjust highlight action interface
 */
export interface IAdjustHighlightAction extends INonUndoableAction {
    highlightType: string;
};

/**
 * Adjust impossible paths action interface
 */
export interface ISetImpossiblePathsAction extends INonUndoableAction {
    impossiblePaths: string[];
};

/**
 * Select page action interface
 */
export interface ISelectPageAction extends INonUndoableAction {
    preserveObjectSelection?: boolean;
    selectedPage: number | undefined;
};

/**
 * Select object action interface
 */
export interface ISelectObjectAction extends INonUndoableAction {
    selectedObject: number;
    childKey?: number;
    forceSingleSelect?: boolean;
    isMultiSelect?: boolean;
    noDeselect?: boolean;
};

/**
 * Set aspect ratio action interface
 */
export interface ISetAspectRatioAction extends INonUndoableAction {
    shapeId: string;
    aspectRatio: number;
    isMultiSelect?: boolean;
};

/**
 * Delete aspect ratio action interface
 */
export interface IDeleteAspectRatioAction extends INonUndoableAction {
    shapeId: string;
};

/**
 * Set all aspect ratios action interface
 */
export interface ISetAllAspectRatiosAction extends INonUndoableAction {
    aspectRatioMap: { [key: string]: number };
};

/**
 * Clear subgroup selection action interface
 */
export interface IClearSubgroupAction extends INonUndoableAction {
}

/**
 * Deselect object action interface
 */
export interface IDeselectObjectAction extends INonUndoableAction {
    deselectedObject: number;
};

/**
 * Deselect all objects action interface
 */
export interface IDeselectAllObjectAction extends INonUndoableAction {
}

/**
 * Select objects by index action interface
 */
export interface ISelectObjectsByIndexAction extends INonUndoableAction {
    selectedPageObjects: number[];
}

/**
 * Set shape tool action interface.
 */
export interface ISetShapeDefaultPropertiesAction extends INonUndoableAction {
    /**
     * These will be default properties used for drawing shape.
     */
    shapeProperties: IShapeProperties;
}

/**
 * Set text default styles action interface.
 */
export interface ISetTextStylesDefaultAction extends INonUndoableAction {
    style: string;
    value: string | undefined;
}

/**
 * Set shape draw tool action interface.
 */
export interface ISetShapeDrawToolAction extends INonUndoableAction {
    /**
     * Set the shape type which should be used for drawing shape.
     * Set to null for custom object.
     */
    shapeType?: ShapeType;

    /**
     * Used to decide whether to make shape editable or not on drag complete.
     */
    editOnComplete?: boolean | undefined;

    /**
     *  This flag will be set to true if text mode is selected
     */
    isTextShape?: boolean | undefined;

    /**
     *  This flag will be set to true if shape is a table
     */
    isTableShape?: boolean | undefined;
}

/**
 * Set shape draw tool metadata.
 */
export interface ISetShapeDrawMetadataAction extends INonUndoableAction {
    /**
     *  Metadata for shape, for example if text table, rows and columns
     */
    metadata?: any | undefined;
}

/**
 * Set text editor instance action interface.
 */
export interface ISetTextEditorInstanceAction extends INonUndoableAction {
    textEditorInstance: any;
}

/**
 * Apply text formatting action interface.
 */
export interface IApplyTextFormattingAction extends INonUndoableAction {
    textStyles: any | undefined;
}

/**
 * Set selected shape first char style action.
 */
export interface ISetFirstCharStyleAction extends INonUndoableAction {
    textStyles: any | undefined;
}

/**
 * Set shape text style action interface.
 */
export interface ISetShapeTextStyle extends INonUndoableAction, ISelection {
    style: string;
    value: string;
    mode: string;
    placeholderTextFormat: string;
    isApply?: boolean;
}

/**
 * Set all text default styles action interface.
 */
export interface ISetAllTextDefaultStylesAction extends INonUndoableAction {
    textStyles: IAnyObjectType;
}

/**
 * Adjusts current highlight properties
 * @param state {ISelection} Current state of Selection reducer
 * @param action {IAdjustHighlightAction} Reducer payload to adjust highlights
 * @returns {ISelection} new state for Selection reducer
 */
export const adjustHighlight = (state: ISelection, action: IAdjustHighlightAction): ISelection => {
	const newState: ISelection = _.cloneDeep(state);
	newState.highlightType = action.highlightType;
	return newState;
};

export const setImpossiblePaths = (state: ISelection, action: ISetImpossiblePathsAction): ISelection => {
	const newState: ISelection = _.cloneDeep(state);
	newState.impossiblePaths = action.impossiblePaths;

	return newState;
};

/**
 * Selects a page
 * @param state {ISelection} Current state of Selection reducer
 * @param action {ISelectPageAction} Reducer payload to select a page
 * @param action.selectedPage {Number | undefined} Page index (1 base) to select or undefined.
 * @returns {ISelection} new state for Selection reducer
 */
export const selectPage = (state: ISelection, action: ISelectPageAction): ISelection => {
	const selectedPage = action.selectedPage;
	const selectedPageObjects = action.preserveObjectSelection ? [...state.selectedPageObjects] : [];
	return { ...state, selectedPage, selectedPageObjects };
};

/**
 * Selects a object
 * @param state {ISelection} Current state of Selection reducer
 * @param action {ISelectObjectAction} Reducer payload to select a object
 * @param action.selectedObject {Number} Object index to select.
 * @param action.isMultiSelect {Number}
 * @returns {ISelection} new state for Selection reducer
 */
export const selectObject = (state: ISelection, action: ISelectObjectAction): ISelection => {
	const newState: ISelection = { ...state };
	const selectedObject = action.selectedObject;
	let selectedPageObjects: number[];
	selectedPageObjects = [...state.selectedPageObjects];
	let groupSelectionIndices = newState.groupSelectionIndices;
	let foundIndex = selectedPageObjects.indexOf(selectedObject);
	if (typeof action.childKey === "number" && groupSelectionIndices && action.childKey !== -1) {
		foundIndex = groupSelectionIndices.indexOf(action.childKey);

		if (action.forceSingleSelect) {
			groupSelectionIndices = [action.childKey];
		} else if (action.isMultiSelect) {
			if (foundIndex === -1) {
				groupSelectionIndices.push(action.childKey);
			} else if (!action.noDeselect) {
				groupSelectionIndices.splice(foundIndex, 1);
			}
		} else {
			if (groupSelectionIndices.length > 1 && foundIndex > -1) {
				return newState;
			} else {
				groupSelectionIndices = [action.childKey];
			}
		}

		newState.groupSelectionIndices = groupSelectionIndices;
		return newState;
	} else {
		newState.groupSelectionIndices = [];
	}
	if (action.forceSingleSelect) {
		selectedPageObjects = [selectedObject];
	} else if (action.isMultiSelect) {
		newState.groupSelectionIndices = [];
		if (foundIndex === -1) {
			selectedPageObjects.push(selectedObject);
		} else if (!action.noDeselect) {
			selectedPageObjects.splice(foundIndex, 1);
		}
	} else {
		if (selectedPageObjects.length > 1 && foundIndex > -1) {
			return newState;
		} else {
			selectedPageObjects = [selectedObject];
		}
	}
	newState.selectedPageObjects = selectedPageObjects;
	return newState;
};

/**
 * Deselects all objects
 * @param state {ISelection} Current state of Selection reducer
 * @param action {IDeselectObjectAction} Reducer payload to deselect all objects
 * @returns {ISelection} new state for Selection reducer
 */
export const clearSubgroupSelection = (state: ISelection, action: IClearSubgroupAction): ISelection => {
	const newState: ISelection = _.cloneDeep(state);
	newState.groupSelectionIndices = [];
	return newState;
};

/**
 * Deselects a object
 * @param state {ISelection} Current state of Selection reducer
 * @param action {IDeselectObjectAction} Reducer payload to deselect a object
 * @param action.deselectedObject {Number} Object index to deselect.
 * @returns {ISelection} new state for Selection reducer
 */
export const deselectObject = (state: ISelection, action: IDeselectObjectAction): ISelection => {
	// Ensure all tables have all cells deselected, as a reset.
	deselectAllTableElements();
	const newState: ISelection = { ...state };
	const deselectedObject = action.deselectedObject;
	const index = newState.selectedPageObjects.indexOf(deselectedObject, 0);
	if (index > -1) {
		newState.selectedPageObjects.splice(index, 1);
	}
	newState.groupSelectionIndices = [];
	return newState;
};

/**
 * Deselects all objects
 * @param state {ISelection} Current state of Selection reducer
 * @param action {IDeselectObjectAction} Reducer payload to deselect all objects
 * @returns {ISelection} new state for Selection reducer
 */
export const deselectAllObjects = (state: ISelection, action: IDeselectObjectAction): ISelection => {
	// Ensure all tables have all cells deselected, as a reset.
	deselectAllTableElements();
	const newState: ISelection = _.cloneDeep(state);
	newState.groupSelectionIndices = [];
	newState.selectedPageObjects = [];
	newState.aspectRatioMap = {};
	newState.highlightType = undefined;
	return newState;
};

/**
 * Selects objects by index
 * @param state {ISelection} Current state of Selection reducer
 * @param action {String} Reducer payload to select all objects
 * @returns {ISelection} new state for Selection reducer
 */
export const selectObjectsByIndex = (state: ISelection, action: ISelectObjectsByIndexAction): ISelection => {
	const selectedPageObjects: number[] = action.selectedPageObjects;
	if (action.groupSelectionIndices !== undefined) {
		state.groupSelectionIndices = action.groupSelectionIndices;
	}
	return { ...state, selectedPageObjects };
};

/**
 * Set shape defaults in state
 * @param state {ISelection}  Current state of Selection reducer
 * @param action {ISetShapeDefaultPropertiesAction} Reducer payload to set shape defaults
 * @returns ISelection
 */
export const setShapeDefaultProperties = (state: ISelection, action: ISetShapeDefaultPropertiesAction): ISelection => {
	const defaultShapeProperties = _.merge(DEFAULT_SHAPE_PROPERTIES, action.shapeProperties);
	return { ...state, defaultShapeProperties };
};

/**
 * Set text shape defaults properties in state
 * @param state {ISelection}  Current state of Selection reducer
 * @param action {ISetShapeDefaultPropertiesAction} Reducer payload to set text shape defaults
 * @returns ISelection
 */
export const setTextShapeDefaultProperties =
    (state: ISelection, action: ISetShapeDefaultPropertiesAction): ISelection => {
    	const newState: ISelection = _.cloneDeep(state);
    	const shapeProperties = _.defaultsDeep(action.shapeProperties, newState.defaultTextShapeProperties);
    	newState.defaultTextShapeProperties = shapeProperties;
    	return newState;
    };

/**
 * Set text defaults in state
 * If style is a format style e.g. Bold, Italic, Underline, Strikethrough, act as a toggle.
 * If style is anything else, define enumerable property or overwrite.
 * @param state {ISelection}  Current state of Selection reducer
 * @param action {ISetTextStylesDefaultAction} Reducer payload to set text defaults
 * @returns ISelection
 */
export const setTextDefaultStyles = (state: ISelection, action: ISetTextStylesDefaultAction): ISelection => {
	const newState: ISelection = _.cloneDeep(state);
	const { autosize, style, value } = action;
	const defaultTextStyles = newState.defaultTextStyles || {};
	const isFormatStyle =
    style === TAG_STYLES.STRONG ||
    style === TAG_STYLES.EM ||
    style === TAG_STYLES.U ||
    style === TAG_STYLES.S;

	if (autosize) {
		defaultTextStyles[AUTOSIZE_KEY] = autosize;
	} else if (isFormatStyle && defaultTextStyles[style] === value) {
		delete defaultTextStyles[style];
	} else if (!defaultTextStyles[style] && style) {
		Object.defineProperty(defaultTextStyles, style, {
			enumerable: true,
			value: value || style,
		});
	} else if (defaultTextStyles.hasOwnProperty(style) && style) {
		defaultTextStyles[style] = value || style;
	}

	newState.defaultTextStyles = defaultTextStyles;
	return newState;
};

/**
 * Set all text defaults styles from cookies in state
 * @param state {ISelection}  Current state of Selection reducer
 * @param action {ISetAllTextDefaultStylesAction} Reducer payload to set text defaults
 * @returns ISelection
 */
export const setAllTextDefaultStyles = (state: ISelection, action: ISetAllTextDefaultStylesAction): ISelection => {
	const newState: ISelection = _.cloneDeep(state);
	newState.defaultTextStyles = action.textStyles || { };
	return newState;
};

/**
 * Set shape draw tool in state
 * @param state {ISelection}  Current state of Selection reducer
 * @param action {ISetTextStylesDefaultAction} Reducer payload to set draw tool
 * @returns ISelection
 */
export const setShapeDrawTool = (state: ISelection, action: ISetShapeDrawToolAction): ISelection => {
	const newState: ISelection = _.cloneDeep(state);
	if (!action.shapeType && action.isTextShape === undefined && action.editOnComplete === undefined) {
		newState.shapeDrawTools = undefined;
	} else {
		newState.shapeDrawTools = {
			editOnComplete: action.editOnComplete,
			isPlaceholder: action.isPlaceholder,
			isTableShape: action.isTableShape,
			isTextShape: action.isTextShape,
			shapeType: action.shapeType as ShapeType,
		};
	}
	return newState;
};

/**
 * Set shape draw tool metadata in state
 * @param state {ISelection}  Current state of Selection reducer
 * @param action {ISetTextStylesDefaultAction} Reducer payload to set draw tool
 * @returns ISelection
 */
export const setShapeDrawMetadata = (state: ISelection, action: ISetShapeDrawMetadataAction): ISelection => {
	const newState: ISelection = _.cloneDeep(state);
	if (action.metadata === undefined) {
		newState.shapeDrawMetadata = undefined;
	} else {
		newState.shapeDrawMetadata = {
			metadata: action.metadata,
		};
	}
	return newState;
};

/**
 * Store instance of active text editor.
 * @param state {ISelection} Selection state.
 * @param action {ISetTextEditorInstanceAction} Reducer payload.
 */
export const setTextEditorInstance = (state: ISelection, action: ISetTextEditorInstanceAction): ISelection => {
	const { textEditorInstance } = action;
	return { ...state, textEditorInstance };
};

/**
 * Set source shape first character styles in state
 * @param state {ISelection} Selection state.
 * @param action {IApplyTextFormattingAction} Reducer payload.
 */
export const setFormatPainterTextStyles = (state: ISelection, action: IApplyTextFormattingAction): ISelection => {
	const newState: ISelection = _.cloneDeep(state);
	newState.formatPainterTextStyles = {
		textStyles: action.textStyles,
		valueStyles: action.valueStyles,
	};
	return newState;
};

/**
  * Set first character styles in state
  * @param state {ISelection} Selection state.
  * @param action {ISetFirstCharStyleAction} Reducer payload.
  */
export const setFirstCharStyle = (state: ISelection, action: ISetFirstCharStyleAction): ISelection => {
	const newState: ISelection = _.cloneDeep(state);
	newState.shapeFirstCharStyles = action.textStyles;
	return newState;
};

/**
 * Set aspect ratio for shape id in aspectRatioMap
 * @param state {ISelection} Selection state.
 * @param action {ISetAspectRatioAction} Reducer payload.
 */
export const setAspectRatio = (state: ISelection, action: ISetAspectRatioAction): ISelection => {
	const newState: ISelection = _.cloneDeep(state);
	let aspectRatioMap = newState.aspectRatioMap as { [key: string]: number };
	if (action.isMultiSelect) {
		if (aspectRatioMap[action.shapeId]) {
			// When isMultiselect is true and same shape is clicked, then that shape should be deselected,
			// hence removing entry of that shape from aspect ratio map
			delete aspectRatioMap[action.shapeId];
		} else {
			aspectRatioMap[action.shapeId] = action.aspectRatio;
		}
	} else {
		if (!aspectRatioMap[action.shapeId]) {
			// When isMultiselect is false and another shape is clicked, then only that shape should get
			// selected, other selected shapes should be deselected, hence emptying map.
			aspectRatioMap = {};
			aspectRatioMap[action.shapeId] = action.aspectRatio;
		}
	}

	newState.aspectRatioMap = aspectRatioMap;
	return newState;
};

/**
 * Delete aspect ratio for shape id from aspectRatioMap
 * @param state {ISelection} Selection state.
 * @param action {IDeleteAspectRatioAction} Reducer payload.
 */
export const deleteAspectRatio = (state: ISelection, action: IDeleteAspectRatioAction): ISelection => {
	const newState: ISelection = _.cloneDeep(state);
	const aspectRatioMap = newState.aspectRatioMap as { [key: string]: number };

	if (aspectRatioMap[action.shapeId]) {
		delete aspectRatioMap[action.shapeId];
	}

	return newState;
};

/**
 * Set aspect ratios for shape ids in aspectRatioMap
 * @param state {ISelection} Selection state.
 * @param action {ISetAllAspectRatiosAction} Reducer payload.
 */
export const setAllAspectRatios = (state: ISelection, action: ISetAllAspectRatiosAction): ISelection => {
	const newState: ISelection = _.cloneDeep(state);
	newState.aspectRatioMap = action.aspectRatioMap;
	return newState;
};
